import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, Check, X } from 'lucide-react';
import { financeApi } from '../../api/finance';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { INCOME_RECORD_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';
import { formatDate } from '../../utils/formatDate';

const STATUS_LABELS: Record<string, string> = { draft: 'Draft', submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected' };

export function IncomeRecordDetailPage() {
  const { id } = useParams<{ id: string }>();
  const recordId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('finance.income.approve');
  const [approving, setApproving] = useState(false);
  const [selectedFiscalYearId, setSelectedFiscalYearId] = useState<number | null>(null);

  const recordQuery = useQuery({ queryKey: ['income-record', recordId], queryFn: () => financeApi.getIncomeRecord(recordId) });
  const docsQuery = useQuery({ queryKey: ['income-record-documents', recordId], queryFn: () => genericDocumentApi.listFor('income_record', recordId) });
  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears(), enabled: approving });
  const fiscalYearDetailQuery = useQuery({ queryKey: ['finance-fiscal-year-detail', selectedFiscalYearId], queryFn: () => financeApi.getFiscalYear(selectedFiscalYearId!), enabled: !!selectedFiscalYearId });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['income-record', recordId] });
  const submitMutation = useMutation({ mutationFn: () => financeApi.submitIncomeRecord(recordId), onSuccess: () => { showSuccess('Submitted.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const approveMutation = useMutation({
    mutationFn: (periodId: number) => financeApi.approveIncomeRecord(recordId, { fiscalYearId: selectedFiscalYearId!, accountingPeriodId: periodId }),
    onSuccess: (result) => { showSuccess(`Approved - journal ${result.data.journalReference}.`); invalidate(); setApproving(false); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to approve.'),
  });
  const rejectMutation = useMutation({ mutationFn: (comment: string) => financeApi.rejectIncomeRecord(recordId, comment), onSuccess: () => { showSuccess('Rejected.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  const periods = fiscalYearDetailQuery.data?.periods ?? [];

  if (!recordQuery.data) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;
  const r = recordQuery.data.data;

  return (
    <div className="page">
      <Link to="/finance/income">&larr; Back to Income</Link>
      <RecordHeader
        title={`Income Record - ${r.income_reference}`}
        entityType="income_record"
        entityId={recordId}
        fields={[
          { label: 'Income date', value: formatDate(r.income_date) },
          { label: 'Amount', value: Number(r.amount).toLocaleString() },
          { label: 'Account', value: r.account_code ? `${r.account_code} - ${r.account_name}` : '-' },
          { label: 'Received into', value: r.bank_account_name ?? '-' },
          { label: 'Status', value: <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
        ]}
        actions={
          <span className="row-actions">
            {r.status === 'draft' && <button className="btn-primary" onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}><Send size={16} className="inline-icon" /> Submit</button>}
            {r.status === 'submitted' && <button className="btn-primary" onClick={() => setApproving((v) => !v)}><Check size={16} className="inline-icon" /> Approve</button>}
            {r.status === 'submitted' && <button className="button-danger" onClick={() => { const c = window.prompt('Rejection reason:'); if (c) rejectMutation.mutate(c); }}><X size={16} className="inline-icon" /> Reject</button>}
          </span>
        }
        readOnly={!canManage}
      />

      {approving && (
        <section className="card">
          <h2>Approve Income Record</h2>
          <label>
            Fiscal Year
            <select onChange={(e) => setSelectedFiscalYearId(Number(e.target.value) || null)}>
              <option value="">- select -</option>
              {fiscalYearsQuery.data?.data.map((fy) => <option key={fy.id} value={fy.id}>{fy.year_label}</option>)}
            </select>
          </label>
          <label>
            Accounting Period
            <select onChange={(e) => { if (e.target.value) approveMutation.mutate(Number(e.target.value)); }}>
              <option value="">- select to approve -</option>
              {periods.map((p) => <option key={p.id} value={p.id}>{p.period_label}</option>)}
            </select>
          </label>
          <button type="button" className="link-button" onClick={() => setApproving(false)}>Cancel</button>
        </section>
      )}

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                <section className="card">
                  <h3>Description</h3>
                  <p>{r.description}</p>
                </section>
                <CommentsThread entityType="income_record" entityId={recordId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('income_record', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('income_record', recordId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('income_record', documentId)}
                queryKeyToInvalidate={['income-record-documents', recordId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="income_record" entityId={recordId} actionStageMap={INCOME_RECORD_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
